import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useState } from "react";
import type { JSX } from "react";

/** Create-a-status modal. Resets its draft on close so re-opening is a clean
 * slate. Extracted from `SettingsWorkflowsPanel.tsx`. */
export function NewStatusDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (b: { name: string; category: string; color?: string }) => void;
  isSubmitting: boolean;
}): JSX.Element {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("active");
  const [color, setColor] = useState("#3b82f6");

  // Reset form whenever the dialog closes so re-opening gives a clean
  // slate instead of leaking the previous draft.
  const handleClose = () => {
    setName("");
    setCategory("active");
    setColor("#3b82f6");
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), category, color });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>New ticket status</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          Adds a new column to the kanban board. Colour shows in
          badges anywhere the status appears.
        </DialogContentText>

        <Box component="form" id="new-status-form" onSubmit={handleSubmit} sx={{ mt: 2, display: "grid", gap: 1.5 }}>
          <TextField
            autoFocus
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="In review"
            fullWidth
          />
          <TextField
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            helperText="Drives reporting buckets and SLA pause/resume."
            fullWidth
          >
            <MenuItem value="open">open</MenuItem>
            <MenuItem value="active">active</MenuItem>
            <MenuItem value="resolved">resolved</MenuItem>
            <MenuItem value="closed">closed</MenuItem>
          </TextField>
          <Box component="label" sx={{ display: "grid", gap: 0.5, fontSize: 14 }}>
            <Box component="span" sx={{ fontWeight: 500 }}>Colour</Box>
            <Box
              component="input"
              type="color"
              value={color}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColor(e.target.value)}
              sx={{ height: 36, width: 80, cursor: "pointer", borderRadius: 0.5, border: 1, borderColor: "divider", bgcolor: "background.default" }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          form="new-status-form"
          disabled={isSubmitting || !name.trim()}
        >
          Add status
        </Button>
      </DialogActions>
    </Dialog>
  );
}
